import React, { useEffect, useRef } from 'react';
import Button from '../ui/Button';
import Container from '../ui/Container';

const Hero = () => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const heading = headingRef.current;
    const paragraph = paragraphRef.current;
    const buttons = buttonsRef.current;
    const highlight = highlightRef.current;

    if (heading) {
      heading.style.opacity = '0';
      heading.style.transform = 'translateY(20px)';
      setTimeout(() => {
        heading.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        heading.style.opacity = '1';
        heading.style.transform = 'translateY(0)';
      }, 100);
    }

    if (paragraph) {
      paragraph.style.opacity = '0';
      paragraph.style.transform = 'translateY(20px)';
      setTimeout(() => {
        paragraph.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        paragraph.style.opacity = '1';
        paragraph.style.transform = 'translateY(0)';
      }, 300);
    }

    if (buttons) {
      buttons.style.opacity = '0';
      buttons.style.transform = 'translateY(20px)';
      setTimeout(() => {
        buttons.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        buttons.style.opacity = '1';
        buttons.style.transform = 'translateY(0)';
      }, 500);
    }

    if (highlight) {
      highlight.style.opacity = '0';
      highlight.style.transform = 'translateY(20px)';
      setTimeout(() => {
        highlight.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
        highlight.style.opacity = '1';
        highlight.style.transform = 'translateY(0)';
      }, 700);
    }
  }, []);

  return (
    <div className="relative bg-gradient-to-br from-blue-900 to-blue-800 text-white">
      <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2')] bg-cover bg-center opacity-10"></div>
      <div className="relative pt-28 pb-20 md:pt-36 md:pb-28">
        <Container>
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              ref={headingRef} 
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            >
              Connecting Hearts, Empowering Lives
            </h1>
            <p 
              ref={paragraphRef}
              className="text-lg md:text-xl text-blue-100 mb-8"
            >
              At HandsForNeeds, we believe in the power of human connection. Our innovative platforms bridge the gap between service providers and those who need assistance, creating meaningful relationships that transform communities.
            </p>
            <div ref={buttonsRef} className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <Button variant="secondary" size="lg">
                Explore Our Platform
              </Button>
              <Button variant="outline" size="lg" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                Learn More
              </Button>
            </div>
            <div 
              ref={highlightRef}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 transform transition-all duration-300 hover:scale-105">
                <h3 className="text-xl font-bold mb-2">10,000+</h3>
                <p className="text-blue-100">Service Providers</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 transform transition-all duration-300 hover:scale-105">
                <h3 className="text-xl font-bold mb-2">50,000+</h3>
                <p className="text-blue-100">Happy Customers</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 transform transition-all duration-300 hover:scale-105">
                <h3 className="text-xl font-bold mb-2">15+</h3>
                <p className="text-blue-100">Cities Covered</p>
              </div>
            </div>
          </div>
        </Container>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-0 transform rotate-180">
        <svg 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
          className="relative block w-full h-12 md:h-16 lg:h-20"
        >
          <path 
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
            opacity=".25" 
            className="fill-white"
          ></path>
          <path 
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
            opacity=".5" 
            className="fill-white"
          ></path>
          <path 
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
            className="fill-white"
          ></path>
        </svg>
      </div>
    </div>
  );
};

export default Hero;